import React, { useCallback, useContext, useEffect, useState } from "react";
import requestCache from "../../utils/requestCache";
import { mutateErrors } from "../../utils";
import { NotifyMode } from "../notify/NotifyModeEnum";
import { useNotify } from "../notify/useNotify";
import { parseToCardsFrontEndModel } from "./helpers/parseToCardsFrontEndModel";
import useProgramQueriesSecondary from "./hooks/useProgramQueriesSecondary";
import useProgramQueriesHigher from "./hooks/useProgramQueriesHigher";
import { parseProgramListFilterOptionsToBackEndModel } from "./helpers/helperFunctions";
import { FilterFormType } from "./constants";
import {
  ILanguageCourseCardFrontEndModel,
  IProgramCardFrontEndModel,
} from "./types";
import useLanguageCourseQueries from "../language-course/hooks/useLanguageCourseQueries";
import { parseToLanguageCourseCardsFrontEndModel } from "./helpers/parseToLanguageCourseCardsFrontEndModel";
import {
  InitialPaginationConfig,
  IPaginationConfig,
} from "./components/Pagination";

export const CardsPageContext = React.createContext<
  Partial<ReturnType<typeof useCardsPageProvider>>
>({});

export const CardsPageProvider: React.FC = ({ children }) => {
  const provider = useCardsPageProvider();

  return (
    <CardsPageContext.Provider value={provider}>
      {children}
    </CardsPageContext.Provider>
  );
};

export const useCardsPage = function () {
  return useContext(CardsPageContext);
};

export const useCardsPageProvider = function () {
  const [loading, setLoading] = useState(false);
  const [programs, setPrograms] = useState<
    (IProgramCardFrontEndModel | ILanguageCourseCardFrontEndModel)[]
  >([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [filters, setFilters] = useState<Record<string, any>>();
  const [paginationConfig, setPaginationConfig] = useState<IPaginationConfig>(
    InitialPaginationConfig,
  );
  const { fetchAll: getAllProgramsHigher } = useProgramQueriesHigher();
  const { fetchAll: getAllProgramsSecondary } = useProgramQueriesSecondary();
  const { fetchAll: getAllCourses } = useLanguageCourseQueries();
  const snackNotify = useNotify();

  const handleFilterChange = useCallback((values) => {
    setFilters(values);
    setPaginationConfig((prev) => ({
      ...prev,
      page: InitialPaginationConfig.page,
    }));
  }, []);

  const getPrograms = useCallback(
    async (data?) => {
      setLoading(true);

      const filterOptions = parseProgramListFilterOptionsToBackEndModel(data);

      let fetchAll;
      let parser;

      switch (data.filterType) {
        case FilterFormType.HIGHER: {
          fetchAll = getAllProgramsHigher;
          parser = parseToCardsFrontEndModel;
          break;
        }
        case FilterFormType.SECONDARY: {
          fetchAll = getAllProgramsSecondary;
          parser = parseToCardsFrontEndModel;
          break;
        }
        case FilterFormType.COURSE: {
          fetchAll = getAllCourses;
          parser = parseToLanguageCourseCardsFrontEndModel;
          break;
        }
      }

      try {
        const cachingKey = JSON.stringify({
          ...filterOptions,
          ...paginationConfig,
        });
        const { data, totalCount } = await requestCache<{
          data: IProgramCardFrontEndModel[];
          totalCount: number;
        }>(cachingKey, () =>
          fetchAll({ ...filterOptions, ...paginationConfig }).then((response) =>
            parser(response),
          ),
        );
        setPrograms(data);
        setTotalCount(totalCount);
        setLoading(false);
      } catch (e) {
        const { errors, message } = mutateErrors(e);
        snackNotify("Error", message, NotifyMode.ERROR);
        setLoading(false);
        return { ...errors };
      }
    },
    [paginationConfig], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    if (!filters) return;

    getPrograms({ ...filters, ...paginationConfig });
  }, [filters, paginationConfig, getPrograms]);

  return {
    handleFilterChange,
    setPaginationConfig,
    paginationConfig,
    programs,
    totalCount,
    loading,
  };
};
